import { useContext, useMemo } from 'react';
import SummaryMobile from '../../../ui-components/SummaryMobile';
import { bookingState } from '../client-state/booking-state-context';
import useLocalize from '../../../Hooks/useLocalize';
import { formatBookingDate } from '../../../Utils/dates';
import useLocale from '../../../Hooks/use-locale';
import { Heading } from '../../../SubComponents/Text';
import { checkFeatureFlag } from '../../../Utils/utils';
import { FeatureName } from '../../../Utils/graphql-sdk';
import { DateTime } from 'luxon';
import useBookingPrices from '../../../Hooks/use-booking-prices';
import FlexibleRoutingSummary from '../../../ui-components/summary-sections/flexible-routing-summary';
import SummaryInfo from '../../../ui-components/SummaryInfo';
import TermsWithData from './terms-with-data';
import NewsletterWithData from './newsletter-with-data';
import { isSummaryOpenActionTypes } from '../../../redusers/is-summary-open-reducer';
import useBookingShop from '../server-data/use-booking-shop';
import useBookingDockingLocations from '../server-data/use-booking-docking-locations';
import BoatCountDurationSummary from '../../../ui-components/summary-sections/boat-count-duration-summary';
import PeopleDurationSummary from '../../../ui-components/summary-sections/people-duration-summary';
import { shopIds } from '../../../shop-ids';
import GoToCheckoutButtonMobileWithData from './go-to-checkout-button-mobile-with-data';

const SummaryMobileWithData = () => {
  const { data: shop } = useBookingShop();
  const { data: dockingLocations } = useBookingDockingLocations();
  const { state, dispatch } = useContext(bookingState);
  const { locale, language } = useLocale();
  const localize = useLocalize();
  const { bookingPrices } = useBookingPrices();

  const shopHasItems = shop?.items && shop?.items?.length > 0;
  const numberOfSteps = !shopHasItems ? 3 : 4;

  const sailoutTimeFormatted = useMemo(() => {
    if (checkFeatureFlag(shop ?? {}, FeatureName.LocalizedTimeDisplay)) {
      if (state.booking.date) {
        return DateTime.fromISO(state.booking.date)
          .setZone(shop?.timezoneName)
          .setLocale(locale)
          .toLocaleString(DateTime.TIME_SIMPLE);
      }

      return null;
    }

    // Fallback to raw start time
    const fallback: string | null = state.booking.startTime ?? null;

    return fallback;
  }, [locale, shop, state.booking.date, state.booking.startTime]);

  const returnTimeFormatted = useMemo(() => {
    if (checkFeatureFlag(shop ?? {}, FeatureName.LocalizedTimeDisplay)) {
      if (state.booking.date) {
        return DateTime.fromISO(state.booking.date)
          .plus({ hours: state.booking.duration })
          .setZone(shop?.timezoneName)
          .setLocale(locale)
          .toLocaleString(DateTime.TIME_SIMPLE);
      }

      return null;
    }

    // Fallback to raw start time
    const fallback: string | null = state.booking.endTime ?? null;

    return fallback;
  }, [locale, shop, state.booking.date, state.booking.endTime, state.booking.duration]);

  return (
    <SummaryMobile
      heading={localize('product.yourBooking')}
      shopName={shop?.name}
      isSummaryOpen={state.isSummaryOpen}
      setSummaryOpen={(isOpen: boolean) =>
        dispatch({ type: isSummaryOpenActionTypes.SET_IS_OPEN, payload: isOpen })
      }
    >
      {state.booking.date && (
        <Heading color="#fff" mb="1">
          {formatBookingDate({
            date: state.booking.date,
            timezone: shop?.timezoneName,
            language,
          })}
        </Heading>
      )}
      {state.booking.startTime && state.booking.endTime && (
        <Heading highLight mb="2">
          {sailoutTimeFormatted} - {returnTimeFormatted}
        </Heading>
      )}

      {state.booking.shopId === shopIds.GOBOAT_CANAL_TOURS ? (
        <PeopleDurationSummary
          boatCount={state.booking.boatCount}
          duration={state.booking.duration}
          price={bookingPrices.rentalPrice}
        />
      ) : (
        <BoatCountDurationSummary
          boatCount={state.booking.boatCount}
          duration={state.booking.duration}
          price={bookingPrices.rentalPrice}
        />
      )}

      {state.booking.pickupLocationId && state.booking.dropoffLocationId && (
        <FlexibleRoutingSummary
          pickupLocation={dockingLocations?.find(
            (dl) => dl.id === state.booking.pickupLocationId
          )}
          dropoffLocation={dockingLocations?.find(
            (dl) => dl.id === state.booking.dropoffLocationId
          )}
          pickupPrice={bookingPrices.pickupPrice}
          dropoffPrice={bookingPrices.dropoffPrice}
        />
      )}

      <SummaryInfo
        orderEditable={!state.payment.showPaymentModal && state.currentStep >= 3}
        prices={bookingPrices}
        order={state.order}
        items={shop?.items}
        vouchers={state.vouchers}
        payments={state.payment.payments}
        isWarrantyApplied={state.isWarrantyApplied}
        warrantyEditable={!state.payment.showPaymentModal}
      />

      {!state.payment.showPaymentModal && state.currentStep === numberOfSteps && (
        <>
          <TermsWithData textColor="#fff" />

          <NewsletterWithData textColor="#fff" />

          <GoToCheckoutButtonMobileWithData />
        </>
      )}
    </SummaryMobile>
  );
};

export default SummaryMobileWithData;
